import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Icon } from "../../lib/icons";
import {
  Calendar, Clock, Check, ChevronLeft, ChevronRight, Loader2, CreditCard,
  UserPlus, LogIn, AlertCircle, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function isoDate(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function formatFrenchDate(d) {
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}
function euros(cents) {
  return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

export default function ReservationPage() {
  const { settings: s } = useSettings();
  const { customer } = useCustomerAuth();
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled") === "1";

  const [services, setServices] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slotsData, setSlotsData] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [step, setStep] = useState(1); // 1 services · 2 date/slot · 3 info · 4 done
  const [form, setForm] = useState({ customer_email: "", customer_name: "", customer_phone: "", customer_notes: "" });
  const [accountMode, setAccountMode] = useState("guest"); // guest | login | register
  const [regPassword, setRegPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm(f => ({
        ...f,
        customer_email: customer.email || "",
        customer_name: [customer.first_name, customer.last_name].filter(Boolean).join(" "),
        customer_phone: customer.phone || "",
      }));
    }
  }, [customer]);

  useEffect(() => {
    api.get("/bookings/services").then(r => setServices(r.data || [])).catch(() => setServices([]));
  }, []);

  const selectedServices = useMemo(
    () => selectedIds.map(id => services.find(x => x.id === id)).filter(Boolean),
    [selectedIds, services]
  );
  const totalDuration = useMemo(
    () => selectedServices.reduce((n, s) => n + (s.booking_duration_min || 0) + (s.booking_buffer_min || 0), 0),
    [selectedServices]
  );
  const totalPrice = useMemo(
    () => selectedServices.reduce((n, s) => n + (s.booking_price_cents || 0), 0),
    [selectedServices]
  );
  const worstPaymentMode = useMemo(() => {
    const rank = { free: 0, deposit: 1, full: 2 };
    let w = "free";
    for (const s of selectedServices) {
      const m = s.booking_payment_mode || "free";
      if (rank[m] > rank[w]) w = m;
    }
    return w;
  }, [selectedServices]);

  // Week strip
  const weekDays = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  const canGoPrevWeek = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const prev = new Date(weekStart); prev.setDate(prev.getDate() - 7);
    return prev >= today || sameDay(prev, today);
  }, [weekStart]);
  const maxAhead = s?.booking_max_days_ahead || 60;

  useEffect(() => {
    if (step !== 2 || !selectedDate || selectedIds.length === 0) return;
    setLoadingSlots(true);
    setPickedSlot(null);
    api.get("/bookings/slots", { params: { date: isoDate(selectedDate), service_ids: selectedIds.join(",") } })
      .then(r => setSlotsData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoadingSlots(false));
  }, [step, selectedDate, selectedIds]);

  const toggleService = (id) => {
    setSelectedIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
    setPickedSlot(null);
  };

  const goToSlotStep = () => {
    if (selectedIds.length === 0) return toast.error("Choisissez au moins un service");
    setStep(2);
  };
  const goToInfoStep = () => {
    if (!pickedSlot) return toast.error("Choisissez un créneau");
    setStep(3);
  };

  const submit = async () => {
    if (!form.customer_email || !form.customer_name) return toast.error("Email et nom requis");
    if (s?.booking_require_phone && !form.customer_phone) return toast.error("Téléphone requis");
    if (accountMode === "register" && (!regPassword || regPassword.length < 8)) return toast.error("Mot de passe d'au moins 8 caractères");
    if (s?.cgv_required_booking !== false && !cgvAccepted) return toast.error("Vous devez accepter les CGV pour réserver");

    setSubmitting(true);
    try {
      if (accountMode === "login") {
        // Connexion avant réservation
        const { data } = await api.post("/customer/login", { email: form.customer_email, password: regPassword });
        localStorage.setItem("msc_customer_token", data.token);
        toast.success("Connecté — réservation en cours");
      }
      const payload = {
        customer_email: form.customer_email,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_notes: form.customer_notes,
        service_ids: selectedIds,
        scheduled_at: pickedSlot.start,
        origin_url: window.location.origin,
        create_account: accountMode === "register",
        create_account_password: accountMode === "register" ? regPassword : null,
        cgv_accepted: cgvAccepted,
      };
      const { data } = await api.post("/bookings/create", payload);
      if (data.stripe_url) {
        window.location.href = data.stripe_url;
        return;
      }
      // Free booking → go to done step
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  // --- UI ---
  if (s && !s.booking_enabled) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-3" />
        <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Réservation désactivée</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Cette fonctionnalité n'est pas encore activée. Revenez prochainement.</p>
        <Link to="/" className="inline-block mt-6 text-[color:var(--brand-sky)] underline">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] py-8 md:py-14" data-testid="reservation-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)] text-xs uppercase tracking-[0.2em] font-bold">
            <Sparkles className="w-3 h-3" />Réservation en ligne
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-[color:var(--brand-navy)] mt-3">Réservez votre rendez-vous</h1>
          {s?.booking_intro_text && <p className="text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto mt-3">{s.booking_intro_text}</p>}
        </div>

        {cancelled && (
          <Card className="p-4 mb-6 border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />Paiement annulé — vous pouvez reprendre votre réservation.
          </Card>
        )}

        {/* Stepper */}
        <div className="flex items-center justify-between mb-6 text-xs" data-testid="reservation-stepper">
          {["Services", "Créneau", "Informations", "Confirmation"].map((lbl, i) => {
            const active = step === i + 1;
            const done = step > i + 1;
            return (
              <div key={lbl} className="flex-1 flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  done ? "bg-emerald-500 text-white" : active ? "bg-[color:var(--brand-navy)] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                }`}>
                  {done ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <div className={`ml-2 text-[11px] font-semibold uppercase tracking-wider hidden sm:block ${active ? "text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))]"}`}>{lbl}</div>
                {i < 3 && <div className={`flex-1 h-px mx-2 ${done ? "bg-emerald-500" : "bg-[hsl(var(--muted))]"}`} />}
              </div>
            );
          })}
        </div>

        {/* Step 1 — services */}
        {step === 1 && (
          <Card className="p-5 md:p-8 space-y-4">
            <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">1. Choisissez une ou plusieurs prestations</h2>
            {services.length === 0 ? (
              <div className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Aucun service réservable pour l'instant.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {services.map(sv => {
                  const on = selectedIds.includes(sv.id);
                  return (
                    <button
                      key={sv.id}
                      type="button"
                      onClick={() => toggleService(sv.id)}
                      className={`text-left p-4 rounded-xl border-2 transition-all hover:scale-[1.01] ${on ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5" : "border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)]/40"}`}
                      data-testid={`svc-card-${sv.slug || sv.id.slice(0, 6)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center ${on ? "bg-[color:var(--brand-sky)] text-white" : "bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)]"}`}>
                          {on ? <Check className="w-5 h-5" /> : <Icon name={sv.icon || "Wrench"} className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm text-[color:var(--brand-navy)]">{sv.title}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mt-0.5">{sv.booking_description || sv.short_description}</div>
                          <div className="flex items-center gap-2 mt-2 text-xs font-mono">
                            <span className="inline-flex items-center gap-1 text-[color:var(--brand-navy)]"><Clock className="w-3 h-3" />{sv.booking_duration_min} min</span>
                            <span className="font-bold text-[color:var(--brand-navy)]">{sv.booking_price_cents > 0 ? euros(sv.booking_price_cents) : "Gratuit"}</span>
                            {sv.booking_payment_mode === "deposit" && <span className="text-[10px] uppercase tracking-wider text-amber-600">Acompte {sv.booking_deposit_percent}%</span>}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {selectedIds.length > 0 && (
              <div className="pt-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-[color:var(--brand-navy)]">
                  <strong>{selectedIds.length}</strong> prestation{selectedIds.length > 1 ? "s" : ""} · <strong>{totalDuration} min</strong> · <strong>{euros(totalPrice)}</strong>
                </div>
                <Button onClick={goToSlotStep} className="bg-[color:var(--brand-navy)] font-bold" data-testid="next-to-slots">
                  Continuer <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Step 2 — date + slot */}
        {step === 2 && (
          <Card className="p-5 md:p-8 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">2. Choisissez un créneau</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" />Modifier</Button>
            </div>

            {/* Week navigation */}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }} disabled={!canGoPrevWeek}>
                <ChevronLeft className="w-3.5 h-3.5 mr-1" />Sem. précédente
              </Button>
              <div className="text-xs font-mono text-[hsl(var(--muted-foreground))]">{formatFrenchDate(weekDays[0])} → {formatFrenchDate(weekDays[6])}</div>
              <Button variant="outline" size="sm" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>
                Sem. suivante<ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {weekDays.map(d => {
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const past = d < today;
                const tooFar = Math.floor((d - today) / 86400000) > maxAhead;
                const active = selectedDate && sameDay(d, selectedDate);
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    onClick={() => !past && !tooFar && setSelectedDate(new Date(d))}
                    disabled={past || tooFar}
                    className={`p-2 rounded-lg text-center transition-all ${active ? "bg-[color:var(--brand-navy)] text-white" : past || tooFar ? "opacity-30 cursor-not-allowed" : "bg-white hover:bg-[color:var(--brand-sky)]/10 border border-[hsl(var(--border))]"}`}
                    data-testid={`day-${isoDate(d)}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</div>
                    <div className="text-lg font-bold">{d.getDate()}</div>
                    <div className="text-[10px]">{d.toLocaleDateString("fr-FR", { month: "short" })}</div>
                  </button>
                );
              })}
            </div>

            {selectedDate && (
              <div className="pt-4 border-t">
                <div className="text-sm font-bold text-[color:var(--brand-navy)] mb-3 capitalize">{formatFrenchDate(selectedDate)}</div>
                {loadingSlots ? (
                  <div className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />Chargement…</div>
                ) : !slotsData?.slots?.length ? (
                  <div className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Aucun créneau ce jour.</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                    {slotsData.slots.map(slot => {
                      const on = pickedSlot?.start === slot.start;
                      return (
                        <button
                          key={slot.start}
                          type="button"
                          onClick={() => slot.available && setPickedSlot(slot)}
                          disabled={!slot.available}
                          className={`h-10 text-sm font-mono rounded-lg border transition-all ${
                            on ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" :
                            slot.available ? "bg-white hover:bg-[color:var(--brand-sky)]/10 border-[hsl(var(--border))]" :
                            "opacity-30 cursor-not-allowed line-through bg-[hsl(var(--muted))]"
                          }`}
                          data-testid={`slot-${slot.label}`}
                        >
                          {slot.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {pickedSlot && (
              <div className="pt-4 border-t flex items-center justify-between">
                <div className="text-sm">Créneau : <strong className="text-[color:var(--brand-navy)]">{pickedSlot.label}</strong> · {totalDuration} min</div>
                <Button onClick={goToInfoStep} className="bg-[color:var(--brand-navy)] font-bold" data-testid="next-to-info">
                  Continuer<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Step 3 — Customer info */}
        {step === 3 && (
          <Card className="p-5 md:p-8 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">3. Vos coordonnées</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" />Modifier</Button>
            </div>

            {!customer && (
              <div className="flex flex-wrap gap-2 p-3 bg-[hsl(var(--muted))]/40 rounded-lg">
                <button type="button" onClick={() => setAccountMode("guest")} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${accountMode === "guest" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white"}`} data-testid="mode-guest">
                  Sans compte
                </button>
                <button type="button" onClick={() => setAccountMode("register")} className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${accountMode === "register" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white"}`} data-testid="mode-register">
                  <UserPlus className="w-3 h-3" />Créer un compte
                </button>
                <button type="button" onClick={() => setAccountMode("login")} className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${accountMode === "login" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white"}`} data-testid="mode-login">
                  <LogIn className="w-3 h-3" />J'ai déjà un compte
                </button>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Nom complet *</Label>
                <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="booking-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} data-testid="booking-email" />
              </div>
              <div>
                <Label>Téléphone {s?.booking_require_phone ? "*" : ""}</Label>
                <Input type="tel" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} data-testid="booking-phone" />
              </div>
              {(accountMode === "register" || accountMode === "login") && !customer && (
                <div>
                  <Label>Mot de passe {accountMode === "register" ? "(min. 8 car.)" : ""} *</Label>
                  <Input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} data-testid="booking-password" />
                </div>
              )}
            </div>

            <div>
              <Label>Informations complémentaires</Label>
              <Textarea rows={3} value={form.customer_notes} onChange={e => setForm({ ...form, customer_notes: e.target.value })} placeholder="Précisions utiles pour l'intervenant…" data-testid="booking-notes" />
            </div>

            <div className="pt-4 border-t">
              <div className="rounded-lg bg-[color:var(--brand-sky)]/5 border border-[color:var(--brand-sky)]/30 p-3 text-sm space-y-1">
                <div className="font-bold text-[color:var(--brand-navy)]">Récapitulatif</div>
                <div>📍 {selectedServices.map(s => s.title).join(" · ")}</div>
                <div>🕑 {formatFrenchDate(selectedDate)} à <strong>{pickedSlot?.label}</strong> ({totalDuration} min)</div>
                <div>💶 Total : <strong>{euros(totalPrice)}</strong>
                  {worstPaymentMode === "deposit" && <span className="text-amber-700 ml-2">· Acompte à régler en ligne</span>}
                  {worstPaymentMode === "full" && <span className="text-amber-700 ml-2">· Paiement en ligne obligatoire</span>}
                  {worstPaymentMode === "free" && <span className="text-emerald-700 ml-2">· Aucun paiement en ligne</span>}
                </div>
              </div>
            </div>

            {s?.cgv_required_booking !== false && (
              <label className="flex items-start gap-2.5 p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 text-[11px] cursor-pointer hover:border-[color:var(--brand-sky)]/50 transition" data-testid="booking-cgv-wrapper">
                <input
                  type="checkbox"
                  checked={cgvAccepted}
                  onChange={(e) => setCgvAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[color:var(--brand-sky)] cursor-pointer shrink-0"
                  data-testid="booking-cgv-checkbox"
                />
                <span className="text-[hsl(var(--foreground))] leading-snug">
                  {s?.cgv_checkbox_label || "J'ai lu et j'accepte les conditions générales de vente"}
                  <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="ml-1 underline font-semibold text-[color:var(--brand-sky)] hover:text-[color:var(--brand-navy)]" data-testid="booking-cgv-link">Lire les CGV</Link>
                </span>
              </label>
            )}

            <div className="flex justify-end pt-3">
              <Button onClick={submit} disabled={submitting || (s?.cgv_required_booking !== false && !cgvAccepted)} className="bg-[color:var(--brand-navy)] font-bold h-11 px-6" data-testid="booking-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : worstPaymentMode === "free" ? <Check className="w-4 h-4 mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                {worstPaymentMode === "free" ? "Valider la réservation" : "Continuer vers le paiement"}
              </Button>
            </div>
          </Card>
        )}

        {/* Step 4 — Done (free flow) */}
        {step === 4 && (
          <Card className="p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <Check className="w-8 h-8" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Réservation enregistrée !</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-md mx-auto">Vous recevrez un email de confirmation à <strong>{form.customer_email}</strong>. Nous vous contacterons si besoin.</p>
            <Link to="/" className="inline-block mt-6 text-[color:var(--brand-sky)] font-semibold underline">Retour à l'accueil</Link>
          </Card>
        )}
      </div>
    </div>
  );
}
